import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import GridLegacy from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import type { Theme } from '@mui/material/styles';
import Upload from '@mui/icons-material/Upload';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import setTitle from 'src/utils/setTitle';
import { createStyles, makeStyles } from '@mui/styles';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import { Button } from 'src/common';
import EyeIcon from 'src/icons/Eye';
import EyeOffIcon from 'src/icons/EyeOff';
import logger from 'src/utils/logger';
import * as Yup from 'yup';
import gtm from 'src/lib/gtm';
import useAuth from 'src/hooks/useAuth';
import { contactsApi } from 'src/api/contactsApi';
import { rolesApi } from 'src/api/rolesApi';
import { userApi } from 'src/api/userApi';
import { useTranslation } from 'react-i18next';
import { applyAuthTokensFromResponse } from 'src/utils/authTokens';
import { PASSWORD_VALIDATION_MESSAGE, REGEX } from 'src/utils/constants';
import SubHeader from 'src/components/SubHeader';
import { OrgSettingsPanel } from 'src/pages/org-settings/org-settings';

const USER_SETTINGS_TAB = {
  user: 0,
  company: 1,
  changePassword: 2,
} as const;

const tabFromSearch = (params: URLSearchParams): number => {
  const tab = (params.get('tab') ?? '').toLowerCase();
  if (tab === 'company' || tab === 'organization' || tab === 'org') {
    return USER_SETTINGS_TAB.company;
  }
  if (tab === 'password' || tab === 'security') {
    return USER_SETTINGS_TAB.changePassword;
  }
  return USER_SETTINGS_TAB.user;
};

const tabToSearchValue = (tabIndex: number): string | null => {
  if (tabIndex === USER_SETTINGS_TAB.company) return 'company';
  if (tabIndex === USER_SETTINGS_TAB.changePassword) return 'password';
  return null;
};

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    pageContainer: {
      backgroundColor: '#F8FAFC',
      minHeight: '100vh',
    },
    headerWrapper: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E2E8F0',
    },
    tabsContainer: {
      margin: '0 auto',
      padding: '10px 10px 0 10px',
    },
    tabs: {
      minHeight: 'auto',
      '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: '3px',
      },
      '& .MuiTabs-flexContainer': {
        justifyContent: 'flex-start',
      },
    },
    tab: {
      textTransform: 'none',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#64748B',
      minWidth: 'auto',
      padding: '16px 20px',
      '&.Mui-selected': {
        color: theme.palette.primary.main,
      },
    },
    mainContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '32px 24px',
    },
    profileCard: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    profileAvatar: {
      width: '70px',
      height: '70px',
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1E293B',
      marginBottom: '4px',
      fontFamily: 'Inter, sans-serif',
    },
    imageGuidelines: {
      fontSize: '14px',
      color: '#64748B',
      fontFamily: 'Inter, sans-serif',
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#1E293B',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      fontFamily: 'Inter, sans-serif',
      '&:hover': {
        opacity: 0.8,
      },
    },
    formContainer: {
      borderRadius: '12px',
      padding: '10px 24px',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1E293B',
      marginBottom: '24px',
      fontFamily: 'Inter, sans-serif',
    },
    formGrid: {
      marginBottom: '16px',
    },
    fieldLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1E293B',
      marginBottom: '8px',
      display: 'block',
      fontFamily: 'Inter, sans-serif',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        height: '44px',
        '& fieldset': {
          borderColor: '#E2E8F0',
        },
        '&:hover fieldset': {
          borderColor: '#CBD5E1',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#3B82F6',
        },
      },
      '& .MuiInputBase-input': {
        padding: '12px 16px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        '&::placeholder': {
          color: '#CBD5E1',
          opacity: 1,
        },
      },
    },
    textAreaField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#E2E8F0',
        },
        '&:hover fieldset': {
          borderColor: '#CBD5E1',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#3B82F6',
        },
      },
      '& .MuiInputBase-input': {
        padding: '12px 16px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        '&::placeholder': {
          color: '#CBD5E1',
          opacity: 1,
        },
      },
    },
    loadingCircle: {
      color: theme.palette.primary.main,
      width: '40px !important',
      height: '40px !important',
      marginLeft: 5,
      marginRight: 5,
    },
    loadingWrapper: {
      textAlign: 'center',
    },
  })
);

const UserSettings: FC = () => {
  setTitle('ITSM | Settings');
  const { t } = useTranslation('accountInfo');
  const { t: tOrg } = useTranslation('orgSettings');
  const classes = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [activeTab, setActiveTab] = useState(() => tabFromSearch(searchParams));
  const { user, logout } = useAuth();

  useEffect(() => {
    setActiveTab(tabFromSearch(searchParams));
  }, [searchParams]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const tab = tabToSearchValue(newValue);
    if (tab) {
      setSearchParams({ tab }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const profileValidationSchema = Yup.object({
    firstName: Yup.string().required(
      t('accountInfo_form_firstNameField_requiredError')
    ),
    lastName: Yup.string().required(
      t('accountInfo_form_lastNameField_requiredError')
    ),
    email: Yup.string()
      .email()
      .required(t('accountInfo_form_emailField_requiredError')),
    role: Yup.array()
      .nullable()
      .test(
        'is-nonempty',
        t('accountInfo_form_roleFiel_requiredError'),
        (value) => value && value?.length > 0
      ),
    image: Yup.string().required(
      t('accountInfo_form_imageField_requiredError')
    ),
    description: Yup.string().max(
      255,
      t('accountInfo_form_bioField_maximumlengthError')
    ),
  });

  const passwordValidationSchema = Yup.object({
    oldPassword: Yup.string()
      .trim()
      .required(t('accountInfo_form_oldPasswordField_requiredError')),
    newPassword: Yup.string()
      .trim()
      .required(t('accountInfo_form_newPasswordField_requiredError'))
      .matches(REGEX.password, PASSWORD_VALIDATION_MESSAGE),
    confirmNewPassword: Yup.string()
      .trim()
      .required(t('accountInfo_form_confirmNewPasswordField_requiredError'))
      .oneOf(
        [Yup.ref('newPassword'), null],
        t('accountInfo_form_confirmNewPasswordField_notMatchingError')
      ),
  });

  const handlePicture = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        return null;
      }

      // Check file type
      const fileType = file.type;
      if (!['image/jpeg', 'image/png'].includes(fileType)) {
        // toast.dismiss();
        toast.error('Invalid File Type. Only PNG and JPG are allowed');
        setIsSubmitting(false);
        return null;
      }

      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        // toast.dismiss();
        toast.error('Max file size is 10MB', {
          duration: 6000,
        });
        setIsSubmitting(false);
        return null;
      }

      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('file', file);
      if (file?.name) {
        formData.append('fileName', file.name);
      }

      const response = await userApi.uploadAttachments(formData);
      const blobUrl = response?.data?.blob_url || response?.data?.fileUrl;
      if (!blobUrl) {
        throw new Error('Upload failed');
      }

      setIsSubmitting(false);
      toast.dismiss();
      toast.success('Image uploaded successfully', {
        duration: 10000,
      });

      return blobUrl;

    } catch (err) {
      setIsSubmitting(false);
      logger(err, 'error');
      toast.dismiss();
      toast.error('Failed to upload image. Please try again.', {
        duration: 10000,
      });
      return null;
    }
  };
  const hasFormChanges = (initialValues, currentValues) => {
    const fieldsToCompare = [
      'firstName',
      'lastName',
      'designation',
      'email',
      'phoneNumber',
      'image',
      'description',
    ];

    return fieldsToCompare.some(
      (field) => initialValues[field] !== currentValues[field]
    );
  };
  const getAllRoles = async () => {
    try {
      const response = await rolesApi.getAllActiveRoles();
      if (response?.status === 200) {
        setRoles(response?.data);
      } else {
        setRoles([]);
      }
    } catch (error) {
      setRoles([]);
    }
  };

  const [showPassword, setShowPassword] = useState<Array<boolean>>([
    false,
    false,
    false,
  ]);

  const handleClickShowPassword = (psType: number) => {
    setShowPassword((prev) => {
      const arrCopy = [...showPassword];
      arrCopy[psType] = !prev[psType];
      return arrCopy;
    });
  };

  const INITIAL_VALUES = {
    name: '',
    firstName: '',
    lastName: '',
    designation: '',
    roles: [],
    email: '',
    contactNumber: '',
    avatar: '',
    info: '',
  };

  const [employeeData, setEmployeeData] = useState(INITIAL_VALUES);

  const getPasswordChangeErrorMessage = (error: any): string =>
    error?.response?.data?.message || 'Unable to update password';

  const handlePasswordSubmit = async (
    values: any,
    resetForm: (next?: any) => void
  ) => {
    try {
      const response = await contactsApi.changeOwnPassword({
        oldPassword: values?.oldPassword?.trim(),
        password: values?.confirmNewPassword?.trim(),
      });

      if (response?.status !== 200) {
        toast.error('Unable to update password');
        return;
      }

      resetForm({
        values: {
          ...values,
          oldPassword: '',
          newPassword: '',
          confirmNewPassword: '',
        },
      });

      // Prefer staying signed in when BE returns new tokens; otherwise force re-login.
      if (applyAuthTokensFromResponse(response)) {
        toast.success('Password updated successfully');
        return;
      }

      toast.success('Password updated. Please sign in again.');
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(getPasswordChangeErrorMessage(error));
    }
  };

  const handleProfileSubmit = async (
    values: any,
    initialValues: any,
    resetForm: (next?: any) => void
  ) => {
    try {
      if (!hasFormChanges(initialValues, values)) {
        toast('ℹ️ No changes to save', { duration: 6000 });
        return;
      }

      const filter: Record<string, unknown> = {
        name: values?.userName,
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        avatar: values?.image,
        info: values?.description,
        contactNumber: values?.phoneNumber,
        designation: values?.designation?.trim(),
      };

      const response = await contactsApi.updateByIdWithPassword(filter);
      if (response?.status === 200) {
        toast.success('Updated Successfully');
        resetForm({ values });
        await getLoggedInUserData();
      }
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 409 || status === 400) {
        toast.error(message || 'Unable to save changes');
      } else if (status !== 401) {
        toast.error(message || 'Unable to save changes');
      }
    }
  };

  const handleOnSubmit = async (
    values: any,
    initialValues: any,
    resetForm: (next?: any) => void
  ) => {
    if (activeTab === USER_SETTINGS_TAB.changePassword) {
      await handlePasswordSubmit(values, resetForm);
      return;
    }
    await handleProfileSubmit(values, initialValues, resetForm);
  };

  const getLoggedInUserData = async () => {
    try {
      const response = await contactsApi.getById(user?.id);
      if (response?.status === 200) {
        setEmployeeData(response?.data);
      }
    } catch (error) {
      logger(error, 'error');
    }
  };

  useEffect(() => {
    getLoggedInUserData();
    getAllRoles();
    gtm.push({ event: 'page_view' });
  }, []);

  return (
    <Box>
      <Box className={classes.headerWrapper}>
        <SubHeader title="Settings" />
        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
          >
            <Tab label="User Settings" className={classes.tab} />
            <Tab label={tOrg('title')} className={classes.tab} />
            <Tab label="Change Password" className={classes.tab} />
          </Tabs>
        </Box>
      </Box>

      <Box className={classes.pageContainer}>
        <div className={classes.mainContainer}>
          {activeTab === USER_SETTINGS_TAB.company ? (
            <OrgSettingsPanel />
          ) : (
          <Formik
            enableReinitialize
            initialValues={{
              userName: employeeData?.name || '',
              firstName: employeeData?.firstName || '',
              lastName: employeeData?.lastName || '',
              designation: employeeData?.designation || '',
              role: employeeData?.roles || [],
              email: employeeData?.email || '',
              phoneNumber: employeeData?.contactNumber?.[0] || '',
              image: employeeData?.avatar || '',
              description: employeeData?.info || '',
              oldPassword: '',
              newPassword: '',
              confirmNewPassword: '',
            }}
            onSubmit={(values, { resetForm }) => {
              handleOnSubmit(values, {
                userName: employeeData?.name || '',
                firstName: employeeData?.firstName || '',
                lastName: employeeData?.lastName || '',
                designation: employeeData?.designation || '',
                role: employeeData?.roles || [],
                email: employeeData?.email || '',
                phoneNumber: employeeData?.contactNumber?.[0] || '',
                image: employeeData?.avatar || '',
                description: employeeData?.info || '',
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: '',
              }, resetForm);
            }}
            validationSchema={
              activeTab === USER_SETTINGS_TAB.changePassword
                ? passwordValidationSchema
                : profileValidationSchema
            }
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              handleBlur,
              setFieldValue,
            }) => (
              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSubmit(event);
                }}
              >
                {/* Profile Section - Always visible */}


                {/* User Settings Tab */}
                {activeTab === USER_SETTINGS_TAB.user && (
                  <div className={classes.formContainer}>
                    <div className={classes.profileCard}>
                      {isSubmitting ? (
                        <div className={classes.loadingWrapper}>
                          <CircularProgress className={classes.loadingCircle} />
                        </div>
                      ) : (
                        <Avatar
                          className={classes.profileAvatar}
                          src={values?.image || 'https://assets.it22.nl/fortes-energy/admin/dummy_image.png'}
                        />
                      )}
                      <div className={classes.profileInfo}>
                        <div className={classes.profileName}>
                          {values?.firstName && values?.lastName
                            ? `${values.firstName} ${values.lastName}`
                            : 'User Name'}
                        </div>
                        <div className={classes.imageGuidelines}>
                          PNG or JPG no bigger than 10MB.
                        </div>
                      </div>
                      <label htmlFor="image-upload" className={classes.uploadButton}>
                        <Upload />
                        Upload
                        <input
                          id="image-upload"
                          type="file"
                          name="image"
                          onChange={async (e) => {
                            const imageUrl = await handlePicture(e);
                            if (imageUrl) {
                              setFieldValue('image', imageUrl);
                            }
                            // Reset the input so the same file can be uploaded again if needed
                            e.target.value = '';
                          }}
                          accept="image/png, image/jpeg"
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                    <div className={classes.sectionTitle}>Account Information</div>
                    <GridLegacy container spacing={2}>
                      {/* First Row */}
                      <GridLegacy item xs={6}>
                        <label htmlFor='firstName' className={classes.fieldLabel}>
                          {t('accountInfo_form_firstNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          className={classes.textField}
                          placeholder="First Name"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.firstName && errors?.firstName)}
                          helperText={touched?.firstName && errors?.firstName}
                          variant="outlined"
                        />
                      </GridLegacy>
                      <GridLegacy item xs={6}>
                        <label htmlFor='lastName' className={classes.fieldLabel}>
                          {t('accountInfo_form_lastNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='lastName'
                          className={classes.textField}
                          placeholder="Last Name"
                          name="lastName"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.lastName && errors?.lastName)}
                          helperText={touched?.lastName && errors?.lastName}
                          variant="outlined"
                        />
                      </GridLegacy>

                      {/* Second Row */}
                      <GridLegacy item xs={6}>
                        <label htmlFor='userName' className={classes.fieldLabel}>
                          {t('accountInfo_form_userNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          disabled
                          id='userName'
                          className={classes.textField}
                          placeholder="User Name"
                          name="userName"
                          value={values?.userName}
                          onBlur={handleBlur}
                          error={Boolean(touched?.userName && errors?.userName)}
                          helperText={touched?.userName && errors?.userName}
                          variant="outlined"
                        />
                      </GridLegacy>
                      <GridLegacy item xs={6}>
                        <label htmlFor='designationField' className={classes.fieldLabel}>
                          {t('accountInfo_form_designationField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='designationField'
                          className={classes.textField}
                          placeholder="Designation"
                          name="designation"
                          value={values.designation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.designation && errors?.designation
                          )}
                          helperText={touched?.designation && errors?.designation}
                          variant="outlined"
                        />
                      </GridLegacy>

                      {/* Third Row */}
                      <GridLegacy item xs={6}>
                        <label htmlFor='email' className={classes.fieldLabel}>
                          {t('accountInfo_form_emailField_label')}
                        </label>
                        <TextField
                          fullWidth
                          disabled
                          id='email'
                          className={classes.textField}
                          placeholder="Email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.email && errors?.email)}
                          helperText={touched?.email && errors?.email}
                          variant="outlined"
                        />
                      </GridLegacy>
                      <GridLegacy item xs={6}>
                        <label htmlFor='phone' className={classes.fieldLabel}>
                          {t('accountInfo_form_phoneNumberField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='phone'
                          className={classes.textField}
                          placeholder="Phone Number"
                          name="phoneNumber"
                          value={values.phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.phoneNumber && errors?.phoneNumber
                          )}
                          helperText={touched?.phoneNumber && errors?.phoneNumber}
                          variant="outlined"
                        />
                      </GridLegacy>

                      {/* Role Field - Full Width */}
                      <GridLegacy item xs={12}>
                        <FormControl
                          disabled
                          fullWidth
                          error={Boolean(errors.role && touched.role)}>
                          <Autocomplete
                            id="role-autocomplete"
                            options={roles}
                            value={values.role}
                            multiple
                            disabled
                            getOptionLabel={(option) =>
                              Object.keys(option).length !== 0 && option?.name
                            }
                            onChange={(e, value) => setFieldValue('role', value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('accountInfo_form_roleField_label')}
                                variant="outlined"
                                name="role"
                                // placeholder="Placeholder"
                                error={Boolean(errors?.role && touched?.role)}
                                helperText={touched?.role && errors?.role}
                                className={classes.textField}
                                InputLabelProps={{
                                  shrink: true,
                                  style: {
                                    position: 'static',
                                    transform: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#1E293B',
                                    marginBottom: '8px',
                                    fontFamily: 'Inter, sans-serif',
                                  },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  style: {
                                    minHeight: '44px',
                                    height: 'auto',
                                    alignItems: 'flex-start',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </GridLegacy>
                      {/* Bio Field */}
                      <GridLegacy item xs={12}>
                        <label htmlFor='bio' className={classes.fieldLabel}>
                          {t('accountInfo_form_bioField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='bio'
                          className={classes.textAreaField}
                          placeholder="Bio"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.description && errors?.description
                          )}
                          helperText={errors?.description}
                          multiline
                          rows={3}
                          variant="outlined"
                        />
                      </GridLegacy>

                      {/* Save Button */}
                      <GridLegacy item xs={12}>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                          <Button
                            type="button"
                            onClick={() => handleSubmit()}
                          >
                            {t('accountInfo_submitButton_label')}
                          </Button>
                        </Box>
                      </GridLegacy>
                    </GridLegacy>
                  </div>
                )}

                {/* Change Password Tab */}
                {activeTab === USER_SETTINGS_TAB.changePassword && (
                  <div className={classes.formContainer}>
                    <div className={classes.sectionTitle}>Change Password</div>
                    <GridLegacy container spacing={2}>
                      <GridLegacy item xs={12}>
                        <label htmlFor='oldPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_oldPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='oldPassword'
                          className={classes.textField}
                          placeholder="Old Password"
                          name="oldPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.oldPassword && errors.oldPassword
                          )}
                          helperText={touched.oldPassword && errors.oldPassword}
                          type={showPassword[0] ? 'text' : 'password'}
                          value={values?.oldPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  type="button"
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(0)}>
                                  {showPassword[0] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </GridLegacy>
                      <GridLegacy item xs={12}>
                        <label htmlFor='newPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_newPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='newPassword'
                          className={classes.textField}
                          placeholder="New Password"
                          name="newPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.newPassword && errors.newPassword
                          )}
                          helperText={touched.newPassword && errors.newPassword}
                          type={showPassword[1] ? 'text' : 'password'}
                          value={values.newPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  type="button"
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(1)}>
                                  {showPassword[1] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </GridLegacy>
                      <GridLegacy item xs={12}>
                        <label htmlFor='confirmNewPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_confirmNewPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='confirmNewPassword'
                          className={classes.textField}
                          placeholder="Confirm New Password"
                          name="confirmNewPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.confirmNewPassword &&
                            errors.confirmNewPassword
                          )}
                          helperText={
                            touched.confirmNewPassword &&
                            errors.confirmNewPassword
                          }
                          type={showPassword[2] ? 'text' : 'password'}
                          value={values?.confirmNewPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  type="button"
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(2)}>
                                  {showPassword[2] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </GridLegacy>

                      {/* Save Button */}
                      <GridLegacy item xs={12}>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                          <Button
                            type="button"
                            onClick={() => handleSubmit()}
                          >
                            {t('accountInfo_submitButton_label')}
                          </Button>
                        </Box>
                      </GridLegacy>
                    </GridLegacy>
                  </div>
                )}
              </form>
            )}
          </Formik>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default UserSettings;